function cleanText(value) {
  return String(value ?? "").trim();
}

function cleanLower(value) {
  return cleanText(value).toLowerCase();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
}

function timestampValue(value) {
  const timestamp =
    Date.parse(cleanText(value));

  return Number.isFinite(timestamp)
    ? timestamp
    : 0;
}

function safeVerifiedImageDimensions(
  source = {}
) {
  const candidates = [
    source?.verifiedImageDimensions,
    source?.verifiedImageMetadata,
  ];

  for (
    const candidate of candidates
  ) {
    const widthPixels =
      Number(
        candidate?.widthPixels
      );

    const heightPixels =
      Number(
        candidate?.heightPixels
      );

    if (
      Number.isInteger(
        widthPixels
      ) &&
      widthPixels > 0 &&
      Number.isInteger(
        heightPixels
      ) &&
      heightPixels > 0
    ) {
      return {
        widthPixels,
        heightPixels,
      };
    }
  }

  return null;
}


export function normalizeChildrensBookAsset(
  value = {}
) {
  const source =
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
      ? value
      : {};

  return {
    id:
      cleanText(
        source.id ||
        source._id
      ),

    projectId:
      cleanText(
        source.projectId
      ),

    blueprintId:
      cleanText(
        source.blueprintId
      ),

    assetType:
      cleanLower(
        source.assetType
      ),

    purpose:
      cleanLower(
        source.purpose
      ),

    bookMapUnitId:
      cleanText(
        source.bookMapUnitId
      ),

    source:
      cleanLower(
        source.source
      ),

    fileName:
      cleanText(
        source.fileName
      ),

    mimeType:
      cleanLower(
        source.mimeType
      ),

    sizeBytes:
      Math.max(
        0,
        safeNumber(
          source.sizeBytes
        )
      ),

    storageProvider:
      cleanLower(
        source.storageProvider
      ),

    storageBucket:
      cleanText(
        source.storageBucket
      ),

    storagePath:
      cleanText(
        source.storagePath
      ),

    publicUrl:
      cleanText(
        source.publicUrl
      ),

    /*
     * Publication Proof may compare a finished cover's
     * shape with the selected trim size before production.
     *
     * Accept either raw server verification metadata or the
     * already-normalised safe dimensions. This keeps repeated
     * normalisation idempotent while still carrying only width
     * and height into browser-facing asset state.
     */
    verifiedImageDimensions:
      safeVerifiedImageDimensions(
        source
      ),

    status:
      cleanLower(
        source.status ||
        "active"
      ),

    revision:
      Math.max(
        1,
        Math.trunc(
          safeNumber(
            source.revision,
            1
          )
        )
      ),

    replacedAssetId:
      cleanText(
        source.replacedAssetId
      ) || null,

    removedAt:
      source.removedAt || null,

    schemaVersion:
      cleanText(
        source.schemaVersion
      ),

    createdAt:
      source.createdAt || null,

    updatedAt:
      source.updatedAt || null,
  };
}

export function normalizeChildrensBookAssets(
  value
) {
  const rows =
    Array.isArray(value)
      ? value
      : Array.isArray(value?.assets)
        ? value.assets
        : [];

  return rows
    .map(
      normalizeChildrensBookAsset
    )
    .filter(
      (asset) =>
        Boolean(asset.id)
    )
    .sort(
      (left, right) =>
        timestampValue(
          right.updatedAt ||
          right.createdAt
        ) -
        timestampValue(
          left.updatedAt ||
          left.createdAt
        )
    );
}

export function activeChildrensBookAssets(
  value
) {
  return normalizeChildrensBookAssets(
    value
  ).filter(
    (asset) =>
      asset.status === "active"
  );
}

export function currentChildrensBookCover(
  value,
  purpose = "front_cover"
) {
  const expectedPurpose =
    cleanLower(purpose);

  return (
    activeChildrensBookAssets(
      value
    ).find(
      (asset) =>
        asset.assetType ===
          "cover" &&
        asset.purpose ===
          expectedPurpose &&
        Boolean(asset.publicUrl)
    ) || null
  );
}

export function childrensBookIllustrationsByUnit(
  value
) {
  return activeChildrensBookAssets(
    value
  ).reduce(
    (result, asset) => {
      const bookMapUnitId =
        cleanText(
          asset.bookMapUnitId
        );

      if (
        asset.assetType !==
          "illustration" ||
        asset.purpose !==
          "book_map_illustration" ||
        !bookMapUnitId ||
        !asset.publicUrl
      ) {
        return result;
      }

      if (
        !result[
          bookMapUnitId
        ]
      ) {
        result[
          bookMapUnitId
        ] = asset;
      }

      return result;
    },
    {}
  );
}

export function externalChildrensBookAssetDraft({
  assetType,
  purpose,
  bookMapUnitId = "",
  publicUrl,
  fileName = "",
} = {}) {
  return {
    assetType:
      cleanLower(assetType),

    purpose:
      cleanLower(purpose),

    bookMapUnitId:
      cleanText(bookMapUnitId),

    source:
      "external_url",

    fileName:
      cleanText(fileName),

    mimeType: "",
    sizeBytes: 0,

    storageProvider:
      "external",

    storageBucket: "",
    storagePath: "",

    publicUrl:
      cleanText(publicUrl),
  };
}
